import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Container } from '../../packages/core/di/Container';
import { i18n } from '../i18n';
import type { FindWarehousesUseCase, WarehouseResponseDTO } from '../../packages/core/application';
import type { CreateWarehouseUseCase } from '../../packages/core/application/use-cases/CreateWarehouseUseCase';
import type { UpdateWarehouseUseCase } from '../../packages/core/application/use-cases/UpdateWarehouseUseCase';
import type { CreateWarehouseDTO } from '../../packages/core/application/dtos/warehouse/CreateWarehouseDTO';
import type { UpdateWarehouseDTO } from '../../packages/core/application/dtos/warehouse/UpdateWarehouseDTO';

export const useWarehouseStore = defineStore('warehouse', () => {
  // State
  const warehouses = ref<WarehouseResponseDTO[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const activeWarehouses = computed(() => warehouses.value.filter(w => w.status === 'ACTIVE'));
  const warehouseById = computed(() => (id: string) => warehouses.value.find(w => w.id === id));

  // Actions
  const container = Container.getInstance();

  const fetchWarehouses = async (activeOnly = true) => {
    loading.value = true;
    error.value = null;

    try {
      const findWarehousesUseCase = container.get<FindWarehousesUseCase>('FindWarehousesUseCase');
      warehouses.value = await findWarehousesUseCase.execute(activeOnly);
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.fetchWarehouses');
      console.error('Error fetching warehouses:', err);
    } finally {
      loading.value = false;
    }
  };

  const createWarehouse = async (dto: CreateWarehouseDTO) => {
    loading.value = true;
    error.value = null;

    try {
      const useCase = container.get<CreateWarehouseUseCase>('CreateWarehouseUseCase');
      const newWarehouse = await useCase.execute(dto);
      warehouses.value.push(newWarehouse);
      return newWarehouse;
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.createWarehouse');
      console.error('Error creating warehouse:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateWarehouse = async (id: string, dto: UpdateWarehouseDTO) => {
    loading.value = true;
    error.value = null;

    try {
      const useCase = container.get<UpdateWarehouseUseCase>('UpdateWarehouseUseCase');
      const updated = await useCase.execute(id, dto);
      const index = warehouses.value.findIndex(w => w.id === id);
      if (index !== -1) {
        warehouses.value[index] = updated;
      }
      return updated;
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.updateWarehouse');
      console.error('Error updating warehouse:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  const reset = () => {
    warehouses.value = [];
    loading.value = false;
    error.value = null;
  };

  return {
    // State
    warehouses,
    loading,
    error,

    // Getters
    activeWarehouses,
    warehouseById,

    // Actions
    fetchWarehouses,
    createWarehouse,
    updateWarehouse,
    clearError,
    reset,
  };
});
